import { createApi } from "@reduxjs/toolkit/query/react";

import type {
  IGetUserDepartmentsDataByIdRequest,
  IGetUserDepartmentsDataByIdResponse,
  TAddUserDepartmentsDataRequest,
  TAddUserDepartmentsDataResponse,
  TEditUserDepartmentsDataRequest,
  TEditUserDepartmentsDataResponse,
  TGetAllUserDepartmentsDataResponse,
  TGetDepartmentManagersRequest,
  TGetDepartmentManagersResponse,
} from "@/types/(company)/departments.interface";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiGeneralParams } from "@/types";

export const companyDepartmentsApi = createApi({
  reducerPath: "companyDepartmentsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["getAllUserDepartments", "userDepartmentById", "getDepartmentManagers"],
  endpoints: (builder) => ({
    // Non-paginated: used everywhere for dropdowns/filters
    getAllUserDepartments: builder.query<TGetAllUserDepartmentsDataResponse, IApiGeneralParams>({
      query: (params) => ({
        url: "/company/departments",
        method: "GET",
        params: {
          ...(params?.branchId ? { branchId: params.branchId } : {}),
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.page ? { page: params.page } : {}),
          ...(params?.limit ? { limit: params.limit } : {}),
        },
      }),
      providesTags: ["getAllUserDepartments"],
    }),
    // Paginated: used for the departments list table in settings
    getCompanyDepartmentsPaginated: builder.query<TGetAllUserDepartmentsDataResponse, { page: number; limit: number; search?: string; branchId?: number }>({
      query: ({ page, limit, search, branchId }) => ({
        url: "/company/departments/paginated",
        method: "GET",
        params: {
          page,
          limit,
          ...(search ? { search } : {}),
          ...(branchId !== undefined ? { branchId } : {}),
        },
      }),
      providesTags: ["getAllUserDepartments"],
    }),
    getUserDepartmentsById: builder.query<IGetUserDepartmentsDataByIdResponse, IGetUserDepartmentsDataByIdRequest>({
      query: ({ id }) => ({
        url: `/company/departments/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _err, { id }) => [{ type: "userDepartmentById", id }],
    }),
    addUserDepartment: builder.mutation<TAddUserDepartmentsDataResponse, TAddUserDepartmentsDataRequest>({
      query: (data) => ({
        url: "/company/departments",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["getAllUserDepartments"],
    }),
    editUserDepartment: builder.mutation<TEditUserDepartmentsDataResponse, TEditUserDepartmentsDataRequest>({
      query: ({ id, ...data }) => ({
        url: `/company/departments/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _err, { id }) => [
        "getAllUserDepartments",
        "getDepartmentManagers",
        { type: "userDepartmentById", id },
      ],
    }),
    getDepartmentManagers: builder.query<TGetDepartmentManagersResponse, TGetDepartmentManagersRequest>({
      query: () => ({
        url: "/company/departments/managers",
        method: "GET",
      }),
      providesTags: ["getDepartmentManagers"],
    }),
    deleteUserDepartment: builder.mutation<
      { success: boolean; message: string },
      number
    >({
      query: (id) => ({
        url: `/company/departments/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["getAllUserDepartments", "getDepartmentManagers"],
    }),
    getUserDepartments: builder.infiniteQuery<
      TGetAllUserDepartmentsDataResponse,
      { limit: number; branchId?: number },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (result) => {
          if (!result?.meta) return undefined;
          const { page, lastPage } = result.meta;
          return page < lastPage ? page + 1 : undefined;
        },
      },
      query: ({ queryArg, pageParam }) => ({
        url: "/company/departments/paginated",
        method: "GET",
        params: {
          limit: queryArg.limit,
          page: pageParam,
          ...(queryArg.branchId ? { branchId: queryArg.branchId } : {}),
        },
      }),
    }),
  }),
});

export const {
  useGetAllUserDepartmentsQuery,
  useGetCompanyDepartmentsPaginatedQuery,
  useAddUserDepartmentMutation,
  useEditUserDepartmentMutation,
  useDeleteUserDepartmentMutation,
  useGetDepartmentManagersQuery,
  useGetUserDepartmentsByIdQuery,
  useGetUserDepartmentsInfiniteQuery,
} = companyDepartmentsApi;
